import { Element, useEditor } from '@craftjs/core';
import { useEditorReturnType } from '@craftjs/core/lib/hooks/useEditor';
import { DataType } from '@lmt/shared-types';
import { EditorEmptyBlockIcon } from '@lmt/unicorn-shared/app/components/common/icons/ui-editor/EditorEmptyBlockIcon';
import { useGlobalForms } from '@lmt/unicorn-shared/app/components/global-forms/useGlobalForms';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { createId } from '@lmt/unicorn-shared/app/helpers/create-id';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { SearchInput } from '../../common/ui-kit/inputs/search/SearchInput';
import { FieldBlockConfiguration, UIEditorField } from '../UIEditorTypes';
import { Container } from './blocks/Container';
import { DraggableElement } from './blocks/DraggableElement';
import { EmptyBlock } from './blocks/EmptyBlock';
import { Input } from './blocks/Input';
import { Text } from './blocks/Text';
import { Title } from './blocks/Title';
import {
  UIEditorBlockWidth,
  WidthSelectControl,
} from './blocks/WidthSelectControl';
import { SettingsPanel } from './SettingsPanel';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { ExpansionContainer } from './blocks/ExpansionContainer';
import { CheckboxSettings } from './blocks/CheckboxSettings';
import { Table } from './blocks/Table';

import ContainerIcon from '@lmt/unicorn-shared/app/icons/ui-editor/editor-container.svg?react';
import HeaderIcon from '@lmt/unicorn-shared/app/icons/ui-editor/editor-header.svg?react';
import TextIcon from '@lmt/unicorn-shared/app/icons/ui-editor/editor-text.svg?react';

import styles from './Toolbox.module.scss';
import { DictionaryType } from './blocks/Inputs/types';
import { useTranslation } from 'react-i18next';

export interface ToolboxProps {
  fieldBlocks: FieldBlockConfiguration[];
  dataBlockTypeName: string;
}

export interface ToolSettings {
  size: UIEditorBlockWidth;
  isEditable: boolean;
  isLabel: boolean;
}

const defaultToolSettings: ToolSettings = {
  size: UIEditorBlockWidth.Quarter,
  isEditable: true,
  isLabel: true,
};

export const Toolbox: FC<ToolboxProps> = (props) => {
  const { connectors } = useEditor();
  const i18next = useScopedTranslation('editor.toolbox');
  const [settings, setSettings] = useState(defaultToolSettings);

  // We have to change `key` field of DraggableElements when Settings change.
  // Otherwise DraggableElements cache initial values of settings and don't reflect changes
  const [draggableElementKeyPostfix, setDraggableElementKeyPostfix] =
    useState('');
  const handleSettingsChange = useCallback((newSettings: ToolSettings) => {
    setDraggableElementKeyPostfix(createId());
    setSettings(newSettings);
  }, []);

  return (
    <>
      <SettingsPanel />
      <ExpansionContainer header={i18next.t('header')}>
        <div className={styles.tag}>{i18next.t('description')}</div>
        <div className={styles.blocksContainer}>
          <div className={styles.block}>
            <DraggableElement
              key={'empty_' + draggableElementKeyPostfix}
              title={i18next.t('blocks.empty')}
              description={i18next.t('blocks.empty_description')}
              icon={<EditorEmptyBlockIcon />}
              ref={(ref) =>
                connectors.create(ref!, <EmptyBlock size={settings.size} />)
              }
            />
          </div>
          <div className={styles.block}>
            <DraggableElement
              title={i18next.t('blocks.container')}
              description={i18next.t('blocks.container_description')}
              icon={<ContainerIcon />}
              ref={(ref) =>
                connectors.create(
                  ref!,
                  <Element canvas={true} is={Container} />,
                )
              }
            />
          </div>
          <div className={styles.block}>
            <DraggableElement
              title={i18next.t('blocks.title')}
              description={i18next.t('blocks.title_description')}
              icon={<HeaderIcon />}
              ref={(ref) =>
                connectors.create(
                  ref!,
                  <Title text={i18next.t('blocks.default_title')} />,
                )
              }
            />
          </div>
          <div className={styles.block}>
            <DraggableElement
              key={'text_' + draggableElementKeyPostfix}
              title={i18next.t('blocks.text')}
              description={i18next.t('blocks.text_description')}
              icon={<TextIcon />}
              ref={(ref) =>
                connectors.create(
                  ref!,
                  <Text
                    text={i18next.t('blocks.default_text')}
                    size={settings.size}
                  />,
                )
              }
            />
          </div>
        </div>
      </ExpansionContainer>
      {props.fieldBlocks.map((fieldBlock) => (
        <FieldsBlock
          key={
            fieldBlock.dataBlockTypeId +
            i18next.i18n.language +
            props.dataBlockTypeName
          }
          title={fieldBlock.title}
          fields={fieldBlock.fields}
          dataBlockTypeId={fieldBlock.dataBlockTypeId}
          isModificationAllowed={fieldBlock.isModificationAllowed}
          connectors={connectors}
          settings={settings}
          isShared={fieldBlock.isShared}
          dataBlockTypeName={props.dataBlockTypeName}
        />
      ))}

      <ExpansionContainer header={i18next.t('default_settings')}>
        <div className={styles.defaultSettings}>
          <CheckboxSettings
            label={i18next.t('editable')}
            isChecked={settings.isEditable}
            setValue={(newValue) =>
              handleSettingsChange({ ...settings, isEditable: newValue })
            }
          />
          <CheckboxSettings
            label={i18next.t('has_label')}
            isChecked={settings.isLabel}
            setValue={(newValue) =>
              handleSettingsChange({ ...settings, isLabel: newValue })
            }
          />
          <WidthSelectControl
            props={{ size: settings.size }}
            setState={(e) =>
              handleSettingsChange({ ...settings, size: e.size })
            }
          />
        </div>
      </ExpansionContainer>
    </>
  );
};

export const FieldsBlock: React.FC<{
  title: string;
  fields: UIEditorField[];
  dataBlockTypeId: number;
  isModificationAllowed: boolean;
  connectors: useEditorReturnType['connectors'];
  settings: ToolSettings;
  isShared: boolean;
  dataBlockTypeName: string;
}> = (props) => {
  const forms = useGlobalForms();
  const i18next = useScopedTranslation('editor.toolbox');
  const [search, setSearch] = useState<string>('');
  const claims = useClaims();
  const i18n = useTranslation();

  const canEditDataBlockFields = claims.permissions.dataBlockFieldManagement;
  const fields = useMemo(() => {
    const result = props.fields
      .map((field) => {
        return {
          ...field,
          localizedTitle: props.isShared
            ? i18n.t(
                'data-block-fields:' +
                  props.dataBlockTypeName.split('.', 1)[0] +
                  '.shared' +
                  '.' +
                  field.key.toLowerCase(),
                field.key,
              )
            : i18n.t(
                'data-block-fields:' +
                  props.dataBlockTypeName +
                  '.' +
                  field.key.toLowerCase(),
                field.key,
              ),
        };
      })
      .filter((field) =>
        (field.localizedTitle as string)
          .toLowerCase()
          .includes(search.toLowerCase()),
      )
      .sort((a, b) =>
        (a.localizedTitle as string).toString().localeCompare(b.localizedTitle),
      );
    return result;
  }, [props.fields, search, i18n.i18n.language]);
  return (
    <ExpansionContainer
      header={props.title}
      onCreateClick={
        canEditDataBlockFields
          ? () => {
              forms.openDataBlockFieldCreation(props.dataBlockTypeId);
            }
          : undefined
      }
    >
      <div className={styles.searchContainer}>
        <SearchInput
          value={search}
          placeholder={i18next.t('search_placeholder')}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={(e) => e.stopPropagation()}
        />
      </div>
      <div className={styles.inputsContainer}>
        {fields.map((field) => (
          <div
            // key={field.localizedTitle + field.key}
            className={styles.inputType}
          >
            <DraggableElement
              title={field.localizedTitle}
              testId={'dataBlockField'}
              onAction={
                field.type === DataType.Table && canEditDataBlockFields
                  ? () => {
                      forms.openDataBlockFieldEdit(field.dataBlockFieldId);
                    }
                  : undefined
              }
              actionTitle={i18next.t('edit_table_field_button')}
              ref={(ref) =>
                props.connectors.create(
                  ref!,
                  field.type === DataType.Table ? (
                    <Table
                      size={props.settings.size}
                      dataKey={field.key}
                      dataBlockFieldId={field.dataBlockFieldId}
                      isLabel={props.settings.isLabel}
                      isEditable={props.settings.isEditable}
                      isShared={props.isShared}
                    />
                  ) : (
                    <Input
                      size={props.settings.size}
                      dataKey={field.key}
                      dataType={field.type}
                      dataBlockFieldId={field.dataBlockFieldId}
                      dictionaryType={
                        field.type === DataType.Dictionary
                          ? field.isOptionAllowed
                            ? DictionaryType.Combobox
                            : DictionaryType.DropDown
                          : DictionaryType.DropDown
                      }
                      dictionaryTitle={field.dataBlockDictionaryTitle}
                      isLabel={props.settings.isLabel}
                      isEditable={props.settings.isEditable}
                      isShared={props.isShared}
                      isOptionAllowed={field.isOptionAllowed}
                      isSingleValue={field.isSingleValue}
                      isRequired={field.isRequired}
                      isMandatory={field.isRequired ?? false}
                    />
                  ),
                )
              }
            />
          </div>
        ))}
      </div>
    </ExpansionContainer>
  );
};
